/**
 * What a bot is ALLOWED to know (charter: "no cheating with hidden info").
 *
 * Both arena callers used to build the AI's enemy list by hand, and both of them
 * cheated:
 *   - `views/arena-match.ts` handed every bot EVERY alive hero on the whole 80x80
 *     map — exact tile, exact HP, through walls, lava and shroom clouds, no range
 *     limit at all. The comment defended it ("bots used to go blind past 18 tiles
 *     and stopped fighting"), but the fix for a bot that idles is a better patrol,
 *     not omniscience.
 *   - `arena/sim.ts` capped at 18 tiles but did no line-of-sight and no vision cone,
 *     so bots saw through every wall inside the ring.
 *   - Both leaked each rival's `pendingGold`, which is on nobody's screen, and each
 *     rival's exact HP, when a player reads a bar at best and only when close.
 *
 * `nav/perception.ts` had the whole apparatus — cone, supercover LOS, latency, 4 s
 * memory — and zero production callers. This is the one function that uses it, and
 * the one place both callers now get their enemies from, so the headless harness
 * measures the same perception the player actually fights.
 *
 * COST. 25 fighters deciding at 5 Hz is the budget. The cone test is a hypot and an
 * angle (cheap) and runs for everyone; the supercover LOS walk (not cheap) runs only
 * for the nearest `maxLosChecks` cone survivors. Worst case per bot per tick is 24
 * cone tests and 8 LOS walks, not 24 walks.
 */

import { DEFAULT_VISION, Perception, lineOfSight, inVisionCone, type VisionConfig } from '../nav/perception';
import type { EnemyView } from './bot-ai';

/** Everything about a rival that is observable by looking at them. */
export interface SenseEnemy {
  id: string;
  x: number; y: number;
  /** 0..1. Banded before it reaches the AI — a player reads a bar, not a number. */
  hpPct: number;
  /** Kills so far. Public: the killfeed shows every death and who caused it. */
  kills: number;
}

export interface SenseInput {
  selfId: string;
  x: number; y: number;
  /** Heading in radians — where the bot is looking. Drives the forward cone. */
  facing: number;
  nowMs: number;
  /** Per-bot perception memory (latency + 4 s last-known). Lives in BotMemory. */
  perception: Perception;
  /** Last-seen facts per enemy id, stamped at observe time. Lives in BotMemory. */
  facts: Map<string, { hpPct: number; kills: number }>;
  /** Wall / mushroom / rock at this tile? */
  blocks: (tx: number, ty: number) => boolean;
  /** Shroom cloud at this tile? Cumulatively cuts sight — the GDD's "hides you from bots". */
  shroomAt?: (tx: number, ty: number) => boolean;
  vision?: VisionConfig;
  maxLosChecks?: number;
}

/** HP is reported as a coarse band, because that is all a bar tells you. */
const HP_BANDS = 4;
function bandHp(pct: number): number {
  return Math.max(0.05, Math.min(1, Math.round(pct * HP_BANDS) / HP_BANDS));
}

/** Fresher than this and the bot is looking straight at them. */
const FRESH_MS = 260;

/**
 * The enemy list for one bot, this tick. Only what passed cone + LOS + reaction
 * latency, plus anything still inside the 4 s memory at its LAST KNOWN position —
 * so losing a target makes a bot search where it went, not forget it existed.
 */
export function buildEnemyViews(o: SenseInput, enemies: SenseEnemy[]): EnemyView[] {
  const cfg = o.vision ?? DEFAULT_VISION.arena_bot;
  const maxLos = o.maxLosChecks ?? 8;

  // Cheap pass: range + cone, for everyone.
  const cands: { e: SenseEnemy; d: number }[] = [];
  for (const e of enemies) {
    if (e.id === o.selfId) continue;
    const d = Math.hypot(e.x - o.x, e.y - o.y);
    if (d > cfg.range) continue;
    if (!inVisionCone(o.x, o.y, o.facing, e.x, e.y, cfg)) continue;
    cands.push({ e, d });
  }
  // Expensive pass: supercover LOS, nearest few only.
  cands.sort((a, b) => a.d - b.d);
  for (let i = 0; i < cands.length && i < maxLos; i++) {
    const { e } = cands[i];
    if (!lineOfSight(o.x, o.y, e.x, e.y, o.blocks, o.shroomAt)) continue;
    o.perception.observe(o.nowMs, e.id, e.x, e.y);
    o.facts.set(e.id, { hpPct: bandHp(e.hpPct), kills: e.kills });
  }

  o.perception.forget(o.nowMs);
  const out: EnemyView[] = [];
  for (const s of o.perception.actable(o.nowMs)) {
    const f = o.facts.get(s.id);
    if (!f) continue;
    const staleMs = o.nowMs - s.lastSeen;
    out.push({
      id: s.id, x: s.x, y: s.y,
      hpPct: f.hpPct, kills: f.kills,
      dist: Math.hypot(s.x - o.x, s.y - o.y),
      staleMs, visible: staleMs <= FRESH_MS,
    });
  }
  // Drop facts for anyone the perception layer has forgotten, or the map grows all match.
  if (o.facts.size > out.length + 8) {
    for (const id of [...o.facts.keys()]) if (!o.perception.remembers(id, o.nowMs)) o.facts.delete(id);
  }
  return out;
}

/** Heading from a movement delta, keeping the old facing when the bot did not move. */
export function updateFacing(prev: number, dx: number, dy: number): number {
  return Math.hypot(dx, dy) < 0.05 ? prev : Math.atan2(dy, dx);
}
